/**
 * Hours slice (PX-W6.4) — the timesheet rows of every opened project plus
 * the caller's own cross-project entries.
 *
 * Cache model: the standard `fetchCache` discipline — one cache per resource
 * (per-project list, my-hours), `condition` dedupes StrictMode double-mounts
 * and back-navigation, list state initialises EMPTY (`[]`, never null).
 *
 * Mutations are NON-optimistic (decision 4): every write awaits the server's
 * returned row, then invalidates both caches and refetches the affected
 * project's list — with redaction on the wire, a client-side patch of a row
 * it never fully sees is structurally unsafe, so the server truth is the only
 * merge. `mutatingIds`/`creating` drive the in-flight affordances. No reducer
 * performs I/O.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { hoursServiceHttp } from '../services/hoursService.http'
import type { HoursEntry, LogHoursInput, UpdateHoursInput } from '../types'

/** A failed thunk's payload — the i18n-mappable key plus the raw message. */
export interface HoursError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

export interface HoursState {
  /** Per-project entries, keyed by project slug. Starts `{}`; lists start `[]`. */
  byProject: Record<string, HoursEntry[]>
  /** The caller's own entries across projects (`GET /my-hours`). */
  mine: HoursEntry[]
  projectCache: FetchCache
  mineCache: FetchCache
  /** The log form's in-flight flag. */
  creating: boolean
  /** Entry ids with a mutation in flight (edit/delete/submit/decision). */
  mutatingIds: string[]
  error: HoursError | null
}

const initialState: HoursState = {
  byProject: {},
  mine: [],
  projectCache: { ...initialFetchCache },
  mineCache: { ...initialFetchCache },
  creating: false,
  mutatingIds: [],
  error: null
}

/** Map any thrown value to the rejection payload the reducers store. */
const toHoursError = (error: unknown): HoursError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Load one project's entries — exactly once per invalidation. */
export const fetchProjectHours = createAsyncThunk<
  HoursEntry[],
  string,
  { rejectValue: HoursError }
>('hours/fetchProjectHours', async (projectId, { rejectWithValue }) => {
  try {
    return await hoursServiceHttp.list(projectId)
  } catch (error) {
    return rejectWithValue(toHoursError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { hours } = getState() as { hours: HoursState }
    return shouldFetch(hours.projectCache, projectId)
  }
})

/** Load the caller's own entries across projects. */
export const fetchMyHours = createAsyncThunk<HoursEntry[], void, { rejectValue: HoursError }>(
  'hours/fetchMyHours',
  async (_, { rejectWithValue }) => {
    try {
      return await hoursServiceHttp.myHours()
    } catch (error) {
      return rejectWithValue(toHoursError(error))
    }
  },
  {
    condition: (_, { getState }) => {
      const { hours } = getState() as { hours: HoursState }
      return shouldFetch(hours.mineCache, null)
    }
  }
)

/** Log a draft entry, then refetch the project's server truth. */
export const createHours = createAsyncThunk<
  HoursEntry,
  { projectId: string; input: LogHoursInput },
  { rejectValue: HoursError }
>('hours/createHours', async ({ projectId, input }, { dispatch, rejectWithValue }) => {
  try {
    const entry = await hoursServiceHttp.create(projectId, input)
    dispatch(hoursMutated())
    await dispatch(fetchProjectHours(projectId))
    return entry
  } catch (error) {
    return rejectWithValue(toHoursError(error))
  }
})

/**
 * Full-replace update of one entry (an author's edit of a draft, or a
 * decider's D-2 status round-trip), then refetch the project's server truth.
 */
export const updateHours = createAsyncThunk<
  HoursEntry,
  { projectId: string; entryId: string; input: UpdateHoursInput },
  { rejectValue: HoursError }
>('hours/updateHours', async ({ projectId, entryId, input }, { dispatch, rejectWithValue }) => {
  try {
    const entry = await hoursServiceHttp.update(projectId, entryId, input)
    dispatch(hoursMutated())
    await dispatch(fetchProjectHours(projectId))
    return entry
  } catch (error) {
    return rejectWithValue(toHoursError(error))
  }
})

/** Delete a draft, then refetch. */
export const deleteHours = createAsyncThunk<
  void,
  { projectId: string; entryId: string },
  { rejectValue: HoursError }
>('hours/deleteHours', async ({ projectId, entryId }, { dispatch, rejectWithValue }) => {
  try {
    await hoursServiceHttp.remove(projectId, entryId)
    dispatch(hoursMutated())
    await dispatch(fetchProjectHours(projectId))
  } catch (error) {
    return rejectWithValue(toHoursError(error))
  }
})

/** Submit a draft (`POST …/submit`, idempotent on `submitted`), then refetch. */
export const submitHours = createAsyncThunk<
  HoursEntry,
  { projectId: string; entryId: string },
  { rejectValue: HoursError }
>('hours/submitHours', async ({ projectId, entryId }, { dispatch, rejectWithValue }) => {
  try {
    const entry = await hoursServiceHttp.submit(projectId, entryId)
    dispatch(hoursMutated())
    await dispatch(fetchProjectHours(projectId))
    return entry
  } catch (error) {
    return rejectWithValue(toHoursError(error))
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const hoursSlice = createSlice({
  name: 'hours',
  initialState,
  reducers: {
    /**
     * A mutation landed: re-arm both caches so the next read refetches the
     * server truth (the refetch itself is dispatched by the thunk, after this).
     */
    hoursMutated(state) {
      invalidate(state.projectCache)
      invalidate(state.mineCache)
    },
    /**
     * Dismiss a surfaced error and RE-ARM both caches — the fetchCache
     * contract's Retry path ("the error UI rearms it via invalidate").
     * Without the invalidate, a failed POST-MUTATION refetch leaves
     * `loadedKey` matching and `invalidated:false`, so `shouldFetch` skips
     * every retry dispatch and the Retry button is permanently dead (the
     * review-M1 regression): first-load failures recovered only because
     * their `loadedKey` was still null.
     */
    hoursErrorDismissed(state) {
      state.error = null
      invalidate(state.projectCache)
      invalidate(state.mineCache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectHours.pending, (state) => {
        fetchStarted(state.projectCache)
        state.error = null
      })
      .addCase(fetchProjectHours.fulfilled, (state, action) => {
        // Normalised: a malformed payload must never write undefined into
        // list state (downstream selectors destructure without guards).
        state.byProject[action.meta.arg] = action.payload ?? []
        fetchSucceeded(state.projectCache, action.meta.arg)
      })
      .addCase(fetchProjectHours.rejected, (state, action) => {
        fetchFailed(state.projectCache)
        state.error = action.payload ?? null
      })
      .addCase(fetchMyHours.pending, (state) => {
        fetchStarted(state.mineCache)
        state.error = null
      })
      .addCase(fetchMyHours.fulfilled, (state, action) => {
        state.mine = action.payload ?? []
        fetchSucceeded(state.mineCache, null)
      })
      .addCase(fetchMyHours.rejected, (state, action) => {
        fetchFailed(state.mineCache)
        state.error = action.payload ?? null
      })
      .addCase(createHours.pending, (state) => {
        state.creating = true
        state.error = null
      })
      .addCase(createHours.fulfilled, (state) => {
        state.creating = false
      })
      .addCase(createHours.rejected, (state, action) => {
        state.creating = false
        state.error = action.payload ?? null
      })
      .addCase(updateHours.pending, (state, action) => {
        state.mutatingIds.push(action.meta.arg.entryId)
        state.error = null
      })
      .addCase(updateHours.fulfilled, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
      })
      .addCase(updateHours.rejected, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
        state.error = action.payload ?? null
      })
      .addCase(deleteHours.pending, (state, action) => {
        state.mutatingIds.push(action.meta.arg.entryId)
        state.error = null
      })
      .addCase(deleteHours.fulfilled, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
      })
      .addCase(deleteHours.rejected, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
        state.error = action.payload ?? null
      })
      .addCase(submitHours.pending, (state, action) => {
        state.mutatingIds.push(action.meta.arg.entryId)
        state.error = null
      })
      .addCase(submitHours.fulfilled, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
      })
      .addCase(submitHours.rejected, (state, action) => {
        state.mutatingIds = state.mutatingIds.filter((id) => id !== action.meta.arg.entryId)
        state.error = action.payload ?? null
      })
  }
})

export const { hoursMutated, hoursErrorDismissed } = hoursSlice.actions
